<app-page-breadcrumb pageTitle="Configuración de cuenta"></app-page-breadcrumb>

<div class="max-w-3xl mx-auto">
    <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Configuración de cuenta</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Administra la seguridad, integraciones y preferencias de tu cuenta.
        </p>
    </div>

    <!-- Cuentas de correo conectadas -->
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
        <div class="flex items-center gap-2 mb-1">
            <svg class="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">Cuentas de correo conectadas</h2>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-5 ml-7">
            Conecta tu cuenta de correo para usar el módulo de comunicación.
        </p>

        @if (loadingMailAccounts) {
        <div class="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
            </svg>
            Cargando cuentas conectadas...
        </div>
        } @else {
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Gmail -->
            <div class="border border-gray-200 dark:border-gray-800 rounded-xl p-5">
                <div class="flex items-start justify-between gap-3 mb-4">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                            <svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/>
                            </svg>
                        </div>
                        <div>
                            <h3 class="font-medium text-gray-900 dark:text-white">Gmail</h3>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Cuenta de Google</p>
                        </div>
                    </div>

                    <span class="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium"
                        [ngClass]="getProviderStatusClasses('google')">
                        {{ getProviderStatusLabel('google') }}
                    </span>
                </div>

                <div class="space-y-2 mb-5">
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Correo:</span>
                        {{ getProviderAccount('google')?.mailAddress || 'No conectado' }}
                    </p>

                    @if (getProviderAccount('google')?.lastConnectedAt) {
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Última conexión:</span>
                        {{ formatDateTime(getProviderAccount('google')?.lastConnectedAt) }}
                    </p>
                    }

                    @if (getProviderAccount('google')?.lastError) {
                    <p class="text-sm text-red-600 dark:text-red-400">
                        <span class="font-medium">Detalle:</span>
                        {{ getProviderAccount('google')?.lastError }}
                    </p>
                    }

                    @if (isProviderConnected('google')) {
                    <div class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                        <p class="text-xs text-yellow-700 dark:text-yellow-300">
                            Puedes cambiar la cuenta vinculada una sola vez. Si vuelves a cambiarla después, el sistema lo bloqueará.
                        </p>
                    </div>
                    }
                </div>

                <div class="flex flex-wrap gap-3">
                    @if (canDisconnect('google')) {
                    <app-button type="button" [disabled]="isDisconnecting('google')" (click)="onDisconnectMail('google')">
                        @if (isDisconnecting('google')) { Desconectando... } @else { Desconectar }
                    </app-button>
                    }

                    @if (isProviderConnected('google')) {
                    <button type="button" [disabled]="isConnecting('google')" (click)="onConnectMail('google')"
                        class="px-4 py-2 rounded-lg border border-brand-500 text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                        @if (isConnecting('google')) { Cambiando... } @else { Cambiar cuenta }
                    </button>
                    } @else if (canReconnect('google')) {
                    <app-button type="button" [disabled]="isConnecting('google')" (click)="onConnectMail('google')">
                        @if (isConnecting('google')) { Reconectando... } @else { Reconectar Gmail }
                    </app-button>
                    } @else {
                    <app-button type="button" [disabled]="isConnecting('google')" (click)="onConnectMail('google')">
                        @if (isConnecting('google')) { Conectando... } @else { Conectar Gmail }
                    </app-button>
                    }
                </div>
            </div>

            <!-- Microsoft -->
            <div class="border border-gray-200 dark:border-gray-800 rounded-xl p-5">
                <div class="flex items-start justify-between gap-3 mb-4">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                            <svg class="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M2 3h9v9H2V3zm11 0h9v9h-9V3zM2 14h9v9H2v-9zm11 0h9v9h-9v-9z"/>
                            </svg>
                        </div>
                        <div>
                            <h3 class="font-medium text-gray-900 dark:text-white">Microsoft Outlook</h3>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Cuenta de Microsoft</p>
                        </div>
                    </div>

                    <span class="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium"
                        [ngClass]="getProviderStatusClasses('microsoft')">
                        {{ getProviderStatusLabel('microsoft') }}
                    </span>
                </div>

                <div class="space-y-2 mb-5">
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Correo:</span>
                        {{ getProviderAccount('microsoft')?.mailAddress || 'No conectado' }}
                    </p>

                    @if (getProviderAccount('microsoft')?.lastConnectedAt) {
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Última conexión:</span>
                        {{ formatDateTime(getProviderAccount('microsoft')?.lastConnectedAt) }}
                    </p>
                    }

                    @if (getProviderAccount('microsoft')?.lastError) {
                    <p class="text-sm text-red-600 dark:text-red-400">
                        <span class="font-medium">Detalle:</span>
                        {{ getProviderAccount('microsoft')?.lastError }}
                    </p>
                    }

                    @if (isProviderConnected('microsoft')) {
                    <div class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                        <p class="text-xs text-yellow-700 dark:text-yellow-300">
                            Puedes cambiar la cuenta vinculada una sola vez. Si vuelves a cambiarla después, el sistema lo bloqueará.
                        </p>
                    </div>
                    }
                </div>

                <div class="flex flex-wrap gap-3">
                    @if (canDisconnect('microsoft')) {
                    <app-button type="button" [disabled]="isDisconnecting('microsoft')" (click)="onDisconnectMail('microsoft')">
                        @if (isDisconnecting('microsoft')) { Desconectando... } @else { Desconectar }
                    </app-button>
                    }

                    @if (isProviderConnected('microsoft')) {
                    <button type="button" [disabled]="isConnecting('microsoft')" (click)="onConnectMail('microsoft')"
                        class="px-4 py-2 rounded-lg border border-brand-500 text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                        @if (isConnecting('microsoft')) { Cambiando... } @else { Cambiar cuenta }
                    </button>
                    } @else if (canReconnect('microsoft')) {
                    <app-button type="button" [disabled]="isConnecting('microsoft')" (click)="onConnectMail('microsoft')">
                        @if (isConnecting('microsoft')) { Reconectando... } @else { Reconectar Microsoft }
                    </app-button>
                    } @else {
                    <app-button type="button" [disabled]="isConnecting('microsoft')" (click)="onConnectMail('microsoft')">
                        @if (isConnecting('microsoft')) { Conectando... } @else { Conectar Microsoft }
                    </app-button>
                    }
                </div>
            </div>
        </div>
        }
    </div>

    <!-- Cambiar contraseña -->
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
        <div class="flex items-center gap-2 mb-1">
            <svg class="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">Cambiar contraseña</h2>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-5 ml-7">Asegura tu cuenta con una contraseña fuerte.</p>

        <form [formGroup]="passwordForm" (ngSubmit)="onChangePassword()" class="space-y-4">
            <div>
                <label for="currentPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Contraseña actual <span class="text-error-500">*</span>
                </label>
                <input type="password" id="currentPassword" formControlName="currentPassword"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors"
                    [class.border-error-500]="isFieldInvalid('currentPassword')" placeholder="Ingresa tu contraseña actual">
                @if (isFieldInvalid('currentPassword')) {
                <p class="mt-1.5 text-sm text-error-500">{{ getFieldError('currentPassword') }}</p>
                }
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label for="newPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Nueva contraseña <span class="text-error-500">*</span>
                    </label>
                    <input type="password" id="newPassword" formControlName="newPassword"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors"
                        [class.border-error-500]="isFieldInvalid('newPassword')" placeholder="Ingresa la nueva contraseña">
                    @if (isFieldInvalid('newPassword')) {
                    <p class="mt-1.5 text-sm text-error-500">{{ getFieldError('newPassword') }}</p>
                    }
                </div>

                <div>
                    <label for="confirmPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Confirmar nueva contraseña <span class="text-error-500">*</span>
                    </label>
                    <input type="password" id="confirmPassword" formControlName="confirmPassword"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors"
                        [class.border-error-500]="isFieldInvalid('confirmPassword') || passwordMismatch"
                        placeholder="Confirma la nueva contraseña">
                    @if (isFieldInvalid('confirmPassword')) {
                    <p class="mt-1.5 text-sm text-error-500">{{ getFieldError('confirmPassword') }}</p>
                    }
                    @if (passwordMismatch) {
                    <p class="mt-1.5 text-sm text-error-500">Las contraseñas no coinciden</p>
                    }
                </div>
            </div>

            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    Mínimo 8 caracteres. Combina letras, números y símbolos.
                </p>
                <app-button type="submit" [disabled]="savingPassword">
                    @if (savingPassword) {
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Actualizando...
                    } @else {
                    Actualizar contraseña
                    }
                </app-button>
            </div>
        </form>
    </div>

    <!-- Zona de peligro -->
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-error-200 dark:border-error-800 p-6">
        <div class="flex items-center gap-2 mb-1">
            <svg class="w-5 h-5 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01M10.29 3.86l-7.5 13A1 1 0 003.66 18h16.68a1 1 0 00.87-1.5l-7.5-13a1 1 0 00-1.74 0z"></path>
            </svg>
            <h2 class="text-base font-semibold text-error-600 dark:text-error-400">Zona de peligro</h2>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-5 ml-7">Acciones irreversibles sobre tu cuenta.</p>

        <div class="rounded-lg border border-error-200 dark:border-error-800 bg-error-50/50 dark:bg-error-900/10 p-5">
            <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                <div class="flex-1">
                    <h3 class="font-medium text-gray-900 dark:text-white">Eliminar mi cuenta y datos</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Se revocarán tus conexiones OAuth con Google/Microsoft, se eliminarán tus datos personales y se
                        desactivará tu cuenta. Esta acción no se puede deshacer.
                    </p>
                    <div class="mt-3">
                        <label for="deleteConfirm" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Escribe <span class="font-mono font-bold text-error-600 dark:text-error-400">ELIMINAR</span> para confirmar
                        </label>
                        <input type="text" id="deleteConfirm" [(ngModel)]="deleteConfirmText" placeholder="ELIMINAR"
                            autocomplete="off"
                            class="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-error-500 focus:border-error-500 dark:bg-gray-800 dark:text-white transition-colors">
                    </div>
                </div>
                <div class="sm:pt-8">
                    <button type="button" (click)="onDeleteMyData()" [disabled]="deletingData"
                        class="px-4 py-2 rounded-lg bg-error-600 text-white hover:bg-error-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
                        @if (deletingData) {
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none"
                            viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Eliminando...
                        } @else {
                        Eliminar cuenta
                        }
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>
